import toast from 'react-hot-toast';
import { styled } from 'goober';
import { ApiKeyManager, ApiKeyRepresentation } from '@common/api';
import { appColors } from '@base/useTheme';
import { Button } from '@components/button';
import { Confirmation } from '@components/confirmation/Confirmation';
import { Delete, Edit, Plus } from '@components/icons/FarseerIcons';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import { Loading } from '@components/loading/Loading';
import { Modal } from '@components/modal/Modal';
import { useModal } from '@components/modal/ModalProvider';
import { TableList, TableListColumn } from '@components/table-list/TableList';
import { Text } from '@components/typography/Typography';
import { useApiKeys, useDeleteApiKey } from '@data/apiKey/apiKeyActions';
import { useI18nContext } from '@i18n/i18n-react';
import { useFormatting } from '@providers/FormattingProvider';
import { handleApiErrorMessage } from '@util/message';
import { ApiKeyFormModal } from './ApiKeyFormModal';

const DAY_MS = 24 * 60 * 60 * 1000;
const EXPIRES_SOON_DAYS = 7;

// Calendar days in local time; rounding absorbs DST's 23- and 25-hour days.
function calendarDaysUntil(date: Date, now: Date = new Date()): number {
    const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    return Math.round((midnight(date) - midnight(now)) / DAY_MS);
}
// Cells stack two lines; TableList's 32px default row height clips them.
const ROW_HEIGHT = 48;

export interface ApiKeysModalProps {
    userId: number;
    userName: string;
}

const Pill = styled('span')<{ $color: string }>(({ $color }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    width: 'fit-content',
    height: '18px',
    padding: '0 8px',
    borderRadius: '9px',
    fontSize: '11px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: $color,
    background: `${$color}14`,
    border: `1px solid ${$color}33`,
}));

// Expired keys stay listed but read as inactive; the delete action stays at full strength.
const Dimmable = styled('div')<{ $dimmed: boolean }>(({ $dimmed }) => ({
    opacity: $dimmed ? 0.5 : 1,
}));

const ListContainer = styled('div')`
    height: 300px;
    display: flex;
    flex-direction: column;
`;

export function ApiKeysModal({ userId, userName }: ApiKeysModalProps) {
    const { LL } = useI18nContext();
    const { formatTimestamp } = useFormatting();
    const { showModal, hideModal } = useModal();
    const apiKeys = useApiKeys(userId);
    const deleteApiKey = useDeleteApiKey(userId);

    const label = (apiKey: ApiKeyRepresentation) => apiKey.description ?? LL.API_KEY_UNTITLED();

    const confirmDelete = (apiKey: ApiKeyRepresentation) => {
        showModal(
            <Confirmation
                $color="error"
                title={LL.CONFIRM_DELETE_OPERATION()}
                question={LL.API_KEY_DELETE_QUESTION({ name: label(apiKey) })}
                confirmLabel={LL.DELETE()}
                onConfirm={async () => {
                    try {
                        await deleteApiKey.mutateAsync(apiKey.id);
                        toast.success(LL.API_KEY_DELETED());
                        hideModal();
                    } catch (error) {
                        handleApiErrorMessage(error);
                    }
                }}
                onCancel={hideModal}
            />
        );
    };

    const dataColumns: TableListColumn<ApiKeyRepresentation>[] = [
        {
            title: LL.LABEL(),
            width: '3fr',
            getValue: (apiKey) => (
                <FlexCol $gap={2}>
                    <Text $bold={apiKey.description !== null} $muted={apiKey.description === null}>
                        {label(apiKey)}
                    </Text>
                    {apiKey.keyHint && (
                        <Text $muted $size="small" style={{ fontFamily: 'monospace' }}>
                            ••••{apiKey.keyHint}
                        </Text>
                    )}
                </FlexCol>
            ),
        },
        {
            title: LL.API_KEY_CREATED_AT(),
            width: '2fr',
            getValue: (apiKey) => formatTimestamp(apiKey.creationTime, 'minutes'),
        },
        {
            title: LL.API_KEY_CREATED_BY(),
            width: '2fr',
            getValue: (apiKey) =>
                apiKey.managedBy === ApiKeyManager.Overseer ? (
                    <Pill $color={appColors.brand.indigo} title={LL.API_KEY_MANAGED_BY_OVERSEER_HINT()}>
                        {LL.API_KEY_MANAGED_BY_OVERSEER()}
                    </Pill>
                ) : (
                    apiKey.issuer?.displayName ?? LL.UNKNOWN_USER()
                ),
        },
    ];

    const columns: TableListColumn<ApiKeyRepresentation>[] = [
        ...dataColumns.map((column) => ({
            ...column,
            getValue: (apiKey: ApiKeyRepresentation, state: { hovered: boolean }) => (
                <Dimmable $dimmed={apiKey.expired}>{column.getValue(apiKey, state)}</Dimmable>
            ),
        })),
        {
            title: LL.API_KEY_EXPIRES(),
            width: '2fr',
            getValue: (apiKey) => <ExpiryCell apiKey={apiKey} />,
        },
        {
            width: '72px',
            getValue: (apiKey) =>
                // Keys another system manages are neither editable nor deletable here.
                apiKey.managedBy === null ? (
                    <FlexRow $align="center" $gap={4} $justify="end">
                        <Button
                            $type="quaternary"
                            $size="small"
                            icon={<Edit />}
                            aria-label={LL.EDIT_API_KEY()}
                            onClick={() =>
                                showModal(<ApiKeyFormModal userId={userId} userName={userName} apiKey={apiKey} />)
                            }
                        />
                        <Button
                            $type="quaternary"
                            $size="small"
                            $color="error"
                            icon={<Delete />}
                            aria-label={LL.DELETE()}
                            onClick={() => confirmDelete(apiKey)}
                        />
                    </FlexRow>
                ) : null,
        },
    ];

    return (
        <Modal.Content width={820}>
            <Modal.Header title={LL.API_KEYS_FOR_USER({ name: userName })} />
            <Modal.Body>
                <FlexCol $gap={16}>
                    <Text $muted>{LL.API_KEYS_SHOWN_ONCE()}</Text>
                    <FlexRow $align="center" $justify="space-between" $gap={16}>
                        <Text $muted $size="small">
                            {LL.API_KEYS_ROTATE_HINT()}
                        </Text>
                        <Button
                            icon={Plus}
                            title={LL.CREATE_API_KEY()}
                            onClick={() => showModal(<ApiKeyFormModal userId={userId} userName={userName} />)}
                        />
                    </FlexRow>
                    <ListContainer>
                        {apiKeys.isLoading ? (
                            <Loading />
                        ) : apiKeys.data && apiKeys.data.length > 0 ? (
                            <TableList
                                rows={apiKeys.data}
                                columns={columns}
                                keyExtractor={(apiKey) => apiKey.id}
                                itemHeight={ROW_HEIGHT}
                                scrollable
                            />
                        ) : (
                            <Text $muted>{LL.API_KEYS_EMPTY()}</Text>
                        )}
                    </ListContainer>
                    <FlexRow $justify="end">
                        <Button $type="quaternary" title={LL.CLOSE()} onClick={hideModal} />
                    </FlexRow>
                </FlexCol>
            </Modal.Body>
        </Modal.Content>
    );
}

function ExpiryCell({ apiKey }: { apiKey: ApiKeyRepresentation }) {
    const { LL } = useI18nContext();
    const { formatTimestamp } = useFormatting();

    if (apiKey.expiresAt === null) {
        return <Text $muted>{LL.API_KEY_NEVER_EXPIRES()}</Text>;
    }
    const formatted = formatTimestamp(apiKey.expiresAt, 'minutes');
    if (apiKey.expired) {
        return <Pill $color={appColors.error.w500}>{LL.API_KEY_EXPIRED_ON({ date: formatted })}</Pill>;
    }
    const days = calendarDaysUntil(apiKey.expiresAt);
    if (days < EXPIRES_SOON_DAYS) {
        return (
            <FlexCol $gap={2}>
                <span>{formatted}</span>
                <Pill $color={appColors.warning.w500}>
                    {days <= 0 ? LL.API_KEY_EXPIRES_TODAY() : LL.API_KEY_EXPIRES_IN_DAYS({ days })}
                </Pill>
            </FlexCol>
        );
    }
    return <span>{formatted}</span>;
}
